import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { uuidv4 } from "./Utils";
import { clamp } from "./Math";

import EventManager from "./EventManager";
import Grid from "./Grid";
import { createExportMessage } from "./Exporter";

// Icons
import { MdFileDownload, MdZoomIn, MdZoomOut } from "react-icons/md";

const ZoomButtons = styled.div`
  position: absolute;
  right: 1.2rem;
  bottom: 1.2rem;
  box-shadow: 0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.12);
  border-radius: 1.2rem;
  opacity: 0;
  overflow: hidden;
  transition: opacity ease-in-out 0.16s;

  button {
    margin: 0;
    padding: 0.8rem 1.2rem;
    background: #fff;

    &:hover {
      background: #eee;
    }

    &:first-child {
      border-radius: 1.2rem 0 0 1.2rem;
      border-right: 1px solid rgba(0, 0, 0, 0.08);
    }

    &:last-child {
      border-radius: 0 1.2rem 1.2rem 0;
    }
  }
`;

const StyledCanvasContainer = styled.div`
  margin: 2.4rem auto;
  position: relative;

  &:hover {
    ${ZoomButtons} {
      opacity: 1;
    }
  }
`;
const StyledCanvas = styled.canvas`
  display: block;
  margin: 0;
  padding: 0;
  background: transparent;
  border: 3px solid transparent;
  &:hover {
    border: 3px solid $blue;
    cursor: "grab";
    cursor: -webkit-grab;
  }
`;

const _pos = [0, 0];
let _prevPos = null;

// -1 to 1
const anchors = {
  CENTER_CENTER: [0.5, 0.5]
};

const states = {
  IDLE: "idle",
  DRAGGING: "dragging"
};

const getMousePosition = (e) => {
  const { left, top } = e.target.getBoundingClientRect();
  let x = e.pageX - left;
  let y = e.pageY - top;

  // Overwrite with touchevents
  if (e.changedTouches) {
    x = e.changedTouches[0].pageX;
    y = e.changedTouches[0].pageY;
  }
  // _pos.set(x, y);
  _pos[0] = x;
  _pos[1] = y;

  if (!_prevPos) {
    _prevPos = [..._pos];
  }

  const delta = [_prevPos[0] - _pos[0], _prevPos[1] - _pos[1]];

  _prevPos = [..._pos];

  return { point: _pos, delta };
};

export default function Canvas({ config, image, id }) {
  const [settings, setSettings] = useState({
    _id: uuidv4(),
    offsetX: 0,
    offsetY: 0,
    zoom: 1,
    cursor: null,
    ignoreKeypoint: false
  });

  const [currentState, setCurrentState] = useState(states.IDLE);

  const transition = (newState) => {
    setCurrentState(newState);
  };

  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const ctx = useRef(null);

  const { source, width, height, borderRadius } = config;

  const calcOffset = (image, viewport, zoom, anchor) => {
    const { width: imgWidth, height: imgHeight } = image;
    const { width: frameWidth, height: frameHeight } = viewport;

    const offsetX = (imgWidth * zoom - frameWidth) * anchor[0];
    const offsetY = (imgHeight * zoom - frameHeight) * anchor[1];

    return [offsetX, offsetY];
  };

  const roundedRectangle = (
    x = 0,
    y = 0,
    width = 1,
    height = 1,
    borderRadius = [0, 0, 0, 0],
    ctx
  ) => {
    if (!ctx) return;

    // If perfect circle
    if (borderRadius.length === 1 && borderRadius[0] >= width / 2) {
      const r = borderRadius[0];
      ctx.beginPath();
      ctx.ellipse(x + r, y + r, r, r, 0, 0, Math.PI * 2);
      return;
    }

    let tl = borderRadius[0];
    let tr = borderRadius[0];
    let br = borderRadius[0];
    let bl = borderRadius[0];

    if (borderRadius.length === 4) {
      [tl, tr, br, bl] = borderRadius;
    }

    ctx.beginPath();
    ctx.moveTo(x + tl, y);
    ctx.lineTo(x + width - tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + tr);
    ctx.lineTo(x + width, y + height - br);
    ctx.quadraticCurveTo(x + width, y + height, x + width - br, y + height);
    ctx.lineTo(x + bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - bl);
    ctx.lineTo(x, y + tl);
    ctx.quadraticCurveTo(x, y, x + tl, y);
    ctx.closePath();
  };

  const center = () => {
    const [offsetX, offsetY] = calcOffset(
      config.source,
      config,
      settings.zoom,
      anchors.CENTER_CENTER
    );

    setSettings((state) => ({ ...state, offsetX, offsetY }));
  };

  const getRelativePoint = (x, y, maxX, maxY) => {
    const relativePointX = x / maxX;
    const relativePointY = y / maxY;

    return [relativePointX, relativePointY];
  };

  const clear = (ctx, width, height, backgroundColor) => {
    if (!ctx) return;

    ctx.save();

    if (backgroundColor) {
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.clearRect(0, 0, width, height);
    }

    ctx.restore();
  };

  const render = (image, ctx) => {
    if (!ctx || !image) return;

    // Clear Image
    clear(ctx, width, height, "#EEE");

    ctx.save();
    // Draw Image
    const { offsetX, offsetY, zoom } = settings;

    roundedRectangle(0, 0, width, height, borderRadius, ctx);
    ctx.clip();

    // void ctx.drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight);
    ctx.drawImage(
      image,
      0,
      0,
      source.width,
      source.height,
      -offsetX,
      -offsetY,
      source.width * zoom,
      source.height * zoom
    );

    ctx.restore();
  };

  const fit = (zoom) => {
    // Define zoom factor
    let newZoom = zoom;

    const isWidth = source.width < source.height;

    if (isWidth) {
      newZoom = width / source.width;

      // Counter check height
      const newHeight = source.height * newZoom;

      // If too small
      if (newHeight < height) {
        newZoom = height / source.height;
      }
    } else {
      newZoom = height / source.height;

      // Counter check width
      const newWidth = source.width * newZoom;

      // If too small
      if (newWidth < width) {
        newZoom = width / source.width;
      }
    }

    return newZoom;
  };

  const fitToScreen = () => {
    setSettings((state) => {
      const newZoom = fit(state.zoom);

      const [offsetX, offsetY] = calcOffset(
        config.source,
        config,
        newZoom,
        anchors.CENTER_CENTER
      );

      return { ...state, zoom: newZoom, offsetX, offsetY };
    }, center);
  };

  //
  const constrainZoom = (zoom) => {
    const { width: srcWidth, height: srcHeight } = config.source;

    return width >= srcWidth * zoom || height >= srcHeight * zoom;
  };

  // Zoom from current center of the image
  const zoom = (value) => {
    // Constrain zoom to not be smaller than tile canvas

    setSettings((state) => {
      const newZoom = clamp(0.0001, 100, state.zoom + value);

      const delta = newZoom / state.zoom;

      if (constrainZoom(newZoom)) return state;

      const wh = width / 2;
      const hh = height / 2;

      const originX = (offsetX + wh) * delta;
      const originY = (offsetY + hh) * delta;

      return {
        ...state,
        zoom: newZoom,
        offsetX: originX - wh,
        offsetY: originY - hh
      };
    });
  };

  const handleMouseMove = (e) => {
    const { delta } = getMousePosition(e);

    setSettings((state) => {
      const maxX = Math.abs(width - source.width * state.zoom);
      const maxY = Math.abs(height - source.height * state.zoom);

      const offsetX = clamp(0, maxX, state.offsetX + delta[0]);
      const offsetY = clamp(0, maxY, state.offsetY + delta[1]);

      return {
        ...state,
        offsetX,
        offsetY
      };
    });
  };

  const handleMouseUp = () => {
    containerRef.current.removeEventListener("mousemove", handleMouseMove);
    containerRef.current.removeEventListener("mouseup", handleMouseUp);

    setSettings((state) => ({ ...state, cursor: null }));
    transition(states.IDLE);
  };

  const handleMouseDown = (e) => {
    if (!canvasRef.current) return;

    // To register the previous point
    getMousePosition(e);

    containerRef.current.addEventListener("mousemove", handleMouseMove);
    containerRef.current.addEventListener("mouseup", handleMouseUp);

    setSettings((state) => ({ ...state, cursor: "grabbing" }));
    transition(states.DRAGGING);
  };

  const queue = () => {
    EventManager.emit("queue", { from: id, target: canvasRef.current });
  };

  const createMask = (
    options = { width, height, borderRadius: [0, 0, 0, 0] }
  ) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      const { width, height, borderRadius } = options;

      const w = width;
      const h = height;

      canvas.width = w;
      canvas.height = h;

      canvas.style.position = "fixed";
      canvas.style.top = "0px";
      canvas.style.left = "0px";

      ctx.save();

      // Backgroudn
      ctx.fillStyle = "#000";
      ctx.rect(0, 0, w, h);
      ctx.fill();

      ctx.restore();

      ctx.save();
      ctx.beginPath();
      ctx.fillStyle = "#FFF";

      roundedRectangle(0, 0, width, height, borderRadius, ctx);

      ctx.fill();

      // if (rounded) {
      //   const rx = w / 2;
      //   const ry = h / 2;
      //   ctx.ellipse(rx, ry, rx, ry, 0, 0, Math.PI * 2);
      //   ctx.fill();
      // } else {
      //   ctx.rect(0, 0, w, h);
      //   ctx.fill();
      // }

      ctx.restore();

      const dataUrl = canvas.toDataURL("image/jpeg");

      const img = new Image();

      // Might not need it
      img.onload = function () {
        resolve(dataUrl);
      };

      img.src = dataUrl;
    });
  };

  const exportCanvas = async () => {
    const invertedZoom = 1 / settings.zoom;

    const mask = await createMask({ width, height, borderRadius });

    EventManager.emit(
      "export",
      createExportMessage(config.source.file, {
        crop: [
          offsetX * invertedZoom,
          offsetY * invertedZoom,
          config.width * invertedZoom,
          config.height * invertedZoom
        ],
        mask,
        borderRadius,
        size: [config.width, config.height],
        source: {
          width: config.source.width,
          height: config.source.height,
          type: config.label,
          name: config.source.filename
        }
      })
    );
  };

  // On Mount
  useEffect(() => {
    // Register with exporter
    EventManager.emit("registerCanvas", { id });

    // Bind EventEventListener
    EventManager.on("exportAll", queue);

    return () => {
      EventManager.off("exportAll", queue);
      EventManager.emit("unregisterCanvas", { id });
    };
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;
    ctx.current = canvasRef.current.getContext("2d");

    // Bind interactive elements
    containerRef.current.addEventListener("mousedown", handleMouseDown);

    return () => {
      if (!canvasRef.current) return;
      containerRef.current.removeEventListener("mousedown", handleMouseDown);
    };
  }, [canvasRef]);

  useEffect(() => {
    center();
  }, [config.source]);

  const { offsetX, offsetY, cursor, showGrid } = settings;
  const maxX = Math.abs(width - source.width * settings.zoom);
  const maxY = Math.abs(height - source.height * settings.zoom);
  const relativePoint = getRelativePoint(offsetX, offsetY, maxX, maxY);

  render(image, ctx.current);

  return (
    <section>
      <h3>
        {config.label}
        <code>
          {config.width} x {config.height}
        </code>
      </h3>
      <StyledCanvasContainer
        style={{ width, height, cursor }}
        ref={containerRef}
      >
        <StyledCanvas ref={canvasRef} width={width} height={height} />
        <Grid
          width={width}
          height={height}
          showGrid={showGrid}
          point={relativePoint}
          showGuides={currentState === states.DRAGGING}
          config={config}
        />
        <ZoomButtons>
          <button
            onClick={() => {
              zoom(-0.02);
            }}
          >
            <MdZoomOut className="icon" />
          </button>
          <button
            onClick={() => {
              zoom(0.02);
            }}
          >
            <MdZoomIn className="icon" />
          </button>
        </ZoomButtons>
      </StyledCanvasContainer>
      <button onClick={center}>Center</button>
      <button onClick={fitToScreen}>Fit</button>
      <button className="primary" onClick={exportCanvas}>
        <MdFileDownload className="icon" />
      </button>
    </section>
  );
}
